import React from "react";
import { FileText, Microphone, ChatCircleText, Brain, Note, Check, X, Heart } from "@phosphor-icons/react";
import { TYPE_COLORS } from "../../lib/videoDetailFormat";

/**
 * Eric 2026-07-20 (iter162) — Extrait de VideoDetailPage.js (refactor 2500→).
 *
 * Barre d'onglets latérale : Locuteurs / Transcriptions / Synthèse / Q&R /
 * Scripts / Procédures / Objections / Concepts. Chaque onglet a une bulle
 * de comptage (Vincent XLSX #423 — bulles TOUJOURS affichées même à 0).
 *
 * Vincent B690 (Eric 2026-02-08) — Onglet Synthèse ajouté entre
 * Transcriptions et Q&R. Puisqu'il n'y a qu'UNE synthèse par doc, on
 * remplace le badge numérique par un disque vert (check) ou rouge (croix)
 * selon la présence de la synthèse.
 *
 * Vincent B702 (Eric 2026-02-11) — Les libellés des onglets correspondant
 * à des connaissances (Synthèse / Q&R / Scripts / Procédures / Objections
 * / Concepts) reprennent EXACTEMENT la couleur des traits verticaux dans
 * la timeline vidéo (TYPE_COLORS.color). L'onglet actif se différencie
 * uniquement par le POIDS de la police (font-bold) — sa couleur reste la
 * même. Locuteurs et Transcriptions restent en zinc (ils ne représentent
 * pas des unités de connaissance).
 */
const TabButton = ({ testid, active, onClick, icon, label, count, statusBadge, title, textColor }) => {
  // Couleur du libellé : type-color pour les onglets connaissances,
  // sinon fallback zinc-500 (Locuteurs / Transcriptions). L'état actif se
  // signale uniquement par font-bold, jamais par un changement de couleur.
  const inlineColor = textColor || undefined;
  const weightClass = active ? "font-bold" : "font-medium";
  return (
    <button
      onClick={onClick}
      title={title}
      style={inlineColor ? { color: inlineColor } : undefined}
      className={`flex-1 min-w-[110px] flex items-center justify-center gap-1.5 py-3 text-xs ${weightClass} transition-colors relative whitespace-nowrap ${
        textColor ? "" : (active ? "text-zinc-800" : "text-zinc-500 hover:text-zinc-700")
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      {icon}
      <span>{label}</span>
      {count != null && (
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
          style={textColor
            ? { backgroundColor: `${textColor}22`, color: textColor }
            : undefined}
          data-testid={`${testid}-count`}
        >
          {count}
        </span>
      )}
      {statusBadge && (
        <span
          className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${
            statusBadge === "done" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
          }`}
          data-testid={`${testid}-status-badge`}
          aria-label={statusBadge === "done" ? "généré" : "non généré"}
        >
          {statusBadge === "done" ? <Check size={10} weight="bold" /> : <X size={10} weight="bold" />}
        </span>
      )}
      {active && (
        <div
          className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
          style={{ backgroundColor: textColor || "#002FA7" }}
        />
      )}
    </button>
  );
};

export const PanelTabsBar = ({
  activePanel,
  setActivePanel,
  isTextDoc,
  lang,
  knowledgeUnits,
  scriptsList,
  scriptsCount,
  speakerData,
  loadSpeakerData,
  allTranscripts,
  transcriptsLoading,
  loadAllTranscripts,
  favKuIds,
}) => {
  const ku = knowledgeUnits || [];
  const qaCount = ku.filter((u) => u.type === "qa").length;
  // Vincent B804 (Eric 2026-02-13) — Les scripts sont stockés dans la
  // collection `sales_scripts` (séparée de `knowledge_units`) et ne sont
  // JAMAIS dans `knowledge_units`. Avant ce fix, le compteur était donc
  // TOUJOURS à 0. Priorité :
  //  1) `scriptsList.length` (liste réelle publiée par <ScriptsPage embedded>)
  //  2) `scriptsCount` (fourni par GET /api/videos/:id → source de vérité serveur)
  //  3) fallback sur KU (rétro-compat, toujours à 0 en pratique).
  const scriptCountFromKU = ku.filter(
    (u) => u.type === "script" || u.type === "sales_script" || u.type === "sales-script"
  ).length;
  const scriptCount =
    Array.isArray(scriptsList) && scriptsList.length > 0
      ? scriptsList.length
      : (typeof scriptsCount === "number" ? scriptsCount : scriptCountFromKU);
  const procedureCount = ku.filter((u) => u.type === "procedure").length;
  const objectionCount = ku.filter((u) => u.type === "objection" || u.type === "rebuttal").length;
  const conceptCount = ku.filter((u) => u.type === "concept").length;
  // Vincent B690 (Eric 2026-02-08) — Il n'y a qu'UNE synthèse par doc :
  // le badge est un check vert si présente, sinon croix rouge.
  const hasSynthesis = ku.some((u) => u.type === "synthesis");
  // Vincent B849 (Eric 2026-02-14 SPEC STRICK PROD) — Compteur total
  // de favoris sur cette vidéo (KUs + scripts). Le Set `favKuIds` contient
  // les IDs favorisés de l'utilisateur courant.
  const _fav = favKuIds || new Set();
  const favCount = ku.filter((u) => _fav.has(u.id)).length
    + (Array.isArray(scriptsList) ? scriptsList.filter((s) => _fav.has(s.id)).length : 0);

  return (
    <div className="flex border-b border-zinc-200 overflow-x-auto" data-testid="panel-tabs-bar">
      {!isTextDoc && (
        <TabButton
          testid="tab-speakers"
          active={activePanel === "speakers"}
          onClick={() => {
            setActivePanel("speakers");
            if (!speakerData) loadSpeakerData();
          }}
          icon={<Microphone size={14} />}
          label={lang === "fr" ? "Locuteurs" : "Speakers"}
        />
      )}
      {!isTextDoc && (
        <TabButton
          testid="tab-transcript"
          active={activePanel === "transcript"}
          onClick={() => {
            setActivePanel("transcript");
            if (!allTranscripts && !transcriptsLoading) loadAllTranscripts();
          }}
          icon={<FileText size={14} />}
          label={lang === "fr" ? "Transcriptions" : "Transcripts"}
        />
      )}
      {/* Vincent B690 (Eric 2026-02-08) — Onglet Synthèse : entre Transcriptions
          et Q&R, visible pour vidéos ET text_docs. Badge check/croix au lieu de compteur. */}
      {/* Vincent B828bis (Eric 2026-02-14) — Ordre FINAL des onglets Explorateur
          de fichiers / Vidéo ou texte (gauche → droite) :
          Locuteurs · Transcriptions · Synthèse · Concepts · Q&R · Procédures ·
          Scripts de vente · Objections. */}
      <TabButton
        testid="tab-synthesis"
        active={activePanel === "synthesis"}
        onClick={() => setActivePanel("synthesis")}
        icon={<Note size={14} />}
        label={lang === "fr" ? "Synthèse" : "Summary"}
        statusBadge={hasSynthesis ? "done" : "todo"}
        textColor={TYPE_COLORS.synthesis.color}
        title={
          lang === "fr"
            ? (hasSynthesis
                ? "Synthèse pédagogique du document (générée). Cliquez pour l'afficher/éditer/régénérer."
                : "Aucune synthèse générée. Cliquez pour en générer une.")
            : (hasSynthesis ? "Document synthesis (generated)." : "No synthesis yet.")
        }
      />
      <TabButton
        testid="tab-knowledge"
        active={activePanel === "knowledge"}
        onClick={() => setActivePanel("knowledge")}
        icon={<Brain size={14} />}
        label={lang === "fr" ? "Concepts" : "Concepts"}
        count={conceptCount}
        textColor={TYPE_COLORS.concept.color}
        title={
          lang === "fr"
            ? "Concepts financiers extraits (la base de connaissances complète est dans le menu de gauche)"
            : "Extracted financial concepts"
        }
      />
      <TabButton
        testid="tab-qa"
        active={activePanel === "qa"}
        onClick={() => setActivePanel("qa")}
        icon={<span>&#9879;&#65039;</span>}
        label={lang === "fr" ? "Q&R" : "Q&R"}
        count={qaCount}
        textColor={TYPE_COLORS.qa.color}
        title={lang === "fr" ? "Gestion des Q&R : générer et exporter le rapport" : "Q&R management"}
      />
      <TabButton
        testid="tab-procedures"
        active={activePanel === "procedures"}
        onClick={() => setActivePanel("procedures")}
        icon={<span>&#128203;</span>}
        label={lang === "fr" ? "Procédures" : "Procedures"}
        count={procedureCount}
        textColor={TYPE_COLORS.procedure.color}
        title={lang === "fr" ? "Gestion des procédures opérationnelles" : "Operational procedures"}
      />
      <TabButton
        testid="tab-scripts"
        active={activePanel === "scripts"}
        onClick={() => setActivePanel("scripts")}
        icon={<ChatCircleText size={14} />}
        label={lang === "fr" ? "Scripts" : "Scripts"}
        count={scriptCount}
        textColor={TYPE_COLORS.script.color}
        title={
          lang === "fr"
            ? `Scripts de vente pour CETTE vidéo uniquement (compte : ${scriptCount}). Si le nombre paraît incorrect, ouvre la console navigateur et regarde window.__ku_debug pour voir la répartition par type.`
            : "Sales scripts management (this video only)"
        }
      />
      <TabButton
        testid="tab-objections"
        active={activePanel === "objections"}
        onClick={() => setActivePanel("objections")}
        icon={<span>&#128721;</span>}
        label={lang === "fr" ? "Objections" : "Objections"}
        count={objectionCount}
        textColor={TYPE_COLORS.objection.color}
        title={lang === "fr" ? "Objections client & réfutations" : "Objections & rebuttals"}
      />
      {/* Vincent B849 (Eric 2026-02-14 SPEC STRICK PROD) — Onglet
          « Mes favoris » : agrège les ♥ marqués par l'utilisateur sur
          les 5 panneaux (Concepts/Q&R/Procédures/Scripts/Objections).
          Utile pour les apprenants qui reviennent réviser leurs pépites. */}
      <TabButton
        testid="tab-favorites"
        active={activePanel === "favorites"}
        onClick={() => setActivePanel("favorites")}
        icon={<Heart size={14} weight={favCount > 0 ? "fill" : "regular"} />}
        label={lang === "fr" ? "Mes favoris" : "My favorites"}
        count={favCount}
        textColor="#e11d48"
        title={lang === "fr"
          ? "Tous les ♥ que vous avez marqués sur cette vidéo (Concepts, Q&R, Procédures, Scripts, Objections)"
          : "All items you have ♥-marked on this video"}
      />
    </div>
  );
};

export default PanelTabsBar;
